import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import useMutationUpdateGame from '@/modules/game/api/hooks/useMutationUpdateGame'
import { GameQueryKeys } from '@/modules/game/keys'
import { GetGameListTableData } from '@/modules/game/type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Edit, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import * as z from 'zod'

const formSchema = z.object({
	displayName: z.string().min(1, 'กรุณากรอกชื่อเกม'),
})

interface EditNameGameProps {
	row: GetGameListTableData
}

export default function EditNameGame({ row }: EditNameGameProps) {
	const [open, setOpen] = useState(false)
	const { mutate: updateGame } = useMutationUpdateGame()
	const queryClient = useQueryClient()

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			displayName: row.displayName || row.name,
		},
	})

	// Reset form values when dialog opens or row data changes
	useEffect(() => {
		if (open) {
			form.reset({
				displayName: row.displayName || row.name,
			})
		}
	}, [open, row.displayName, row.name, form])

	const onSubmit = (values: z.infer<typeof formSchema>) => {
		updateGame(
			{
				id: row.id,
				payload: {
					displayName: values.displayName,
				},
			},
			{
				onSuccess: () => {
					toast.success('อัพเดทชื่อเกมสำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	const handleReset = () => {
		updateGame(
			{
				id: row.id,
				payload: {
					displayName: row.name,
				},
			},
			{
				onSuccess: () => {
					toast.success('คืนค่าชื่อเกมเป็นค่าเริ่มต้นสำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="icon">
					<Edit className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>แก้ไขชื่อเกม</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="grid gap-4 py-4"
					>
						<div className="text-sm text-muted-foreground">
							ชื่อเริ่มต้น: {row.name}
						</div>
						<FormField
							control={form.control}
							name="displayName"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<Input placeholder="ชื่อเกม" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={handleReset}
								className="flex-1"
							>
								<RotateCcw className="mr-2 h-4 w-4" />
								คืนค่าเริ่มต้น
							</Button>
							<Button
								type="submit"
								disabled={form.formState.isSubmitting}
								className="flex-1"
							>
								บันทึก
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
